// Jun-2026 — Opening Balances (previous-ERP carry-in). P&L-only: sets a
// lump-sum Opening Expenditure + Opening Revenue on a project. No ledger,
// GST or voucher impact — flows only into Project P&L / Performance P&L /
// cost summary. Permission module: project_opening_balance.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, History, Trash2 } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export const ProjectOpeningBalanceDialog = ({ project, onClose, onSaved }) => {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("project_opening_balance", "edit");
  const canDelete = hasPerm("project_opening_balance", "delete");
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ opening_expenditure: "", opening_revenue: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/opening-balance`)
      .then((r) => {
        setDoc(r.data);
        setForm({
          opening_expenditure: r.data.opening_expenditure || "",
          opening_revenue: r.data.opening_revenue || "",
          remarks: r.data.remarks || "",
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load opening balance"));
  }, [project.id]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${project.id}/opening-balance`, {
        opening_expenditure: Number(form.opening_expenditure) || 0,
        opening_revenue: Number(form.opening_revenue) || 0,
        remarks: form.remarks || "",
      });
      setDoc(r.data);
      toast.success("Opening balance saved — P&L reports updated (no accounting impact)");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the opening balance for this project? P&L reports will drop the Opening figures.")) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}/opening-balance`);
      toast.success("Opening balance removed");
      setDoc({ project_id: project.id, opening_expenditure: 0, opening_revenue: 0, remarks: "", history: [] });
      setForm({ opening_expenditure: "", opening_revenue: "", remarks: "" });
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusy(false); }
  };

  const history = (doc?.history || []).slice().reverse();

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="opening-balance-dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-md shadow-2xl border border-purple-300 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-purple-200 bg-purple-50 flex items-center gap-2">
          <div>
            <div className="text-sm font-black text-slate-800">Opening Balance — {project.project_code}</div>
            <div className="text-[10px] text-slate-500">{project.project_name}</div>
          </div>
          <button onClick={onClose} data-testid="opening-balance-close"
            className="ml-auto text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          <div className="px-2.5 py-2 bg-amber-50 border border-amber-200 text-amber-800 text-[10px] leading-snug">
            Carried in from the previous ERP. Affects <b>P&L reports only</b> — no ledger,
            GST, vendor or voucher impact anywhere in Primework.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Opening Expenditure (₹)</div>
            <input type="number" min="0" step="0.01" disabled={!canEdit}
              data-testid="opening-expenditure-input"
              value={form.opening_expenditure}
              onChange={(e) => setForm((f) => ({ ...f, opening_expenditure: e.target.value }))}
              className="w-full border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums focus:border-purple-500 focus:outline-none disabled:bg-slate-50"
              placeholder="0.00" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Opening Revenue — billed in old ERP (₹)</div>
            <input type="number" min="0" step="0.01" disabled={!canEdit}
              data-testid="opening-revenue-input"
              value={form.opening_revenue}
              onChange={(e) => setForm((f) => ({ ...f, opening_revenue: e.target.value }))}
              className="w-full border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums focus:border-purple-500 focus:outline-none disabled:bg-slate-50"
              placeholder="0.00" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Remarks</div>
            <textarea rows={2} disabled={!canEdit}
              data-testid="opening-remarks-input"
              value={form.remarks}
              onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
              className="w-full border border-slate-300 px-2.5 py-1.5 focus:border-purple-500 focus:outline-none disabled:bg-slate-50"
              placeholder="e.g. Tally closing balance as on 31-Mar-2026" />
          </div>
          {doc?.updated_by && (
            <div className="text-[10px] text-slate-400">
              Last set by {doc.updated_by} · {(doc.updated_at || "").slice(0, 10)}
            </div>
          )}
          {history.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                <History size={11} /> Change History
              </div>
              <div className="border border-slate-200 divide-y divide-slate-100 max-h-36 overflow-y-auto" data-testid="opening-history">
                {history.map((h, i) => (
                  <div key={i} className="px-2 py-1.5 text-[10px] text-slate-600 flex justify-between gap-2">
                    <span>Exp {inr(h.opening_expenditure)} · Rev {inr(h.opening_revenue)}</span>
                    <span className="text-slate-400 shrink-0">{h.updated_by} · {(h.updated_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
          {canDelete && (doc?.updated_by || doc?.created_by) && (
            <button onClick={clear} disabled={busy} data-testid="opening-balance-delete"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 border border-rose-300 hover:bg-rose-50 px-3 py-1.5 transition-colors">
              <Trash2 size={11} /> Remove
            </button>
          )}
          {canEdit && (
            <button onClick={save} disabled={busy} data-testid="opening-balance-save"
              className="ml-auto text-[10px] font-bold uppercase tracking-wider text-white bg-purple-700 hover:bg-purple-800 px-4 py-1.5 transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save"}
            </button>
          )}
          <button onClick={onClose}
            className={`text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-300 hover:bg-slate-100 px-3 py-1.5 transition-colors ${!canEdit ? "ml-auto" : ""}`}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectOpeningBalanceDialog;
